'use client';

import { matchesRoutePrefix, stripLocalePrefix } from '@/lib/i18n/locale-path';
import { Routes, getSafePaymentCallback } from '@/routes';
import { useEffect } from 'react';

const SITE_RUNTIME_CHECKOUT_RETURN_PATH_STORAGE_KEY =
  'imgenhancer:site-runtime-checkout-return-path';

function readBrowserPath(defaultPath = '/') {
  if (typeof window === 'undefined') {
    return defaultPath;
  }

  return `${window.location.pathname || defaultPath}${
    window.location.search
  }${window.location.hash}`;
}

function readNormalizedPathname(path: string) {
  const url = new URL(path, 'https://imgenhancer.local');
  return stripLocalePrefix(url.pathname);
}

function normalizeReturnPath(value: string | null | undefined) {
  const safePath = getSafePaymentCallback(value, '');
  if (!safePath) {
    return null;
  }

  const pathname = readNormalizedPathname(safePath);
  if (
    pathname === Routes.Pricing ||
    pathname === Routes.Payment ||
    matchesRoutePrefix(pathname, '/checkout')
  ) {
    return null;
  }

  return safePath;
}

function readRememberedReturnPath() {
  try {
    const storedPath = window.sessionStorage.getItem(
      SITE_RUNTIME_CHECKOUT_RETURN_PATH_STORAGE_KEY
    );
    const safePath = normalizeReturnPath(storedPath);
    if (!safePath && storedPath !== null) {
      window.sessionStorage.removeItem(
        SITE_RUNTIME_CHECKOUT_RETURN_PATH_STORAGE_KEY
      );
    }
    return safePath;
  } catch {
    return null;
  }
}

export function getCurrentSiteRuntimeCheckoutCallback(defaultPath = '/') {
  return readBrowserPath(defaultPath);
}

export function rememberCurrentSiteRuntimeCheckoutReturnPath() {
  if (typeof window === 'undefined') {
    return;
  }

  const returnPath = normalizeReturnPath(readBrowserPath());
  if (!returnPath) {
    return;
  }

  try {
    window.sessionStorage.setItem(
      SITE_RUNTIME_CHECKOUT_RETURN_PATH_STORAGE_KEY,
      returnPath
    );
  } catch {
    // The current page remains the authoritative fallback when storage is unavailable.
  }
}

export function resolveSiteRuntimeCheckoutReturnPath(defaultPath = '/') {
  const currentPath = readBrowserPath(defaultPath);
  if (normalizeReturnPath(currentPath)) {
    return currentPath;
  }

  return readRememberedReturnPath() ?? currentPath;
}

/**
 * Captures the latest non-checkout page before any link, form, or scripted
 * navigation can move the tab into Pricing or a hosted checkout flow.
 */
export function SiteRuntimeCheckoutReturnPathTracker() {
  useEffect(() => {
    const remember = () => rememberCurrentSiteRuntimeCheckoutReturnPath();

    remember();
    document.addEventListener('click', remember, true);
    document.addEventListener('submit', remember, true);
    window.addEventListener('pagehide', remember);
    window.addEventListener('popstate', remember);
    window.addEventListener('hashchange', remember);

    return () => {
      document.removeEventListener('click', remember, true);
      document.removeEventListener('submit', remember, true);
      window.removeEventListener('pagehide', remember);
      window.removeEventListener('popstate', remember);
      window.removeEventListener('hashchange', remember);
    };
  }, []);

  return null;
}
